import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { Button, Checkbox, Line, ModalBody, ModalHeader, P, toast } from "@sorcery/ui";

import { useModal } from "~/providers/ModalProvider";
import { useUser } from "~/providers/UserProvider";
import { SessionStorageKeys } from "~/types/storage";
import { userPath } from "~/utils/paths";
import { api } from "~/utils/trpc";

type Props = {
  deckId: string;
};

export const DeckDeleteModal = ({ deckId }: Props) => {
  const { closeModal } = useModal();
  const router = useRouter();
  const [error, setError] = useState("");
  const [dontAsk, setDontAsk] = useState(false);
  const { user } = useUser();
  const trpcUtils = api.useUtils();

  const { mutateAsync: deleteDeck, error: trpcError } = api.deck.delete.useMutation({
    onSuccess: () => {
      toast("Deck deleted");
      void trpcUtils.user.getBySession.invalidate();
      void trpcUtils.deck.getByUserId.invalidate();
      if (router.asPath.includes(deckId)) {
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        void router.push(userPath(user!.id));
      }
      closeModal();
    },
  });

  // Track trpc errors for display
  useEffect(() => {
    if (trpcError) {
      // don't expose server side error messages
      setError("Something went wrong.");
    }
  }, [trpcError]);

  const onSubmit = () => {
    // clear errs
    setError("");
    if (dontAsk) {
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      sessionStorage.setItem(SessionStorageKeys.DONTASKDELETE, "true");
    }
    void deleteDeck({ deckId });
  };

  return (
    <div className="p-6">
      {/* header */}
      <ModalHeader title="Delete Deck" />

      <ModalBody>
        {/* item */}
        <div className="mb-2 w-full">
          <P>
            <span>Are you 100% sure you want to delete this deck?</span>
          </P>
          <P>This cannot be undone</P>

          <div className="mb-2 flex items-center space-x-2" onClick={() => setDontAsk(!dontAsk)}>
            <Checkbox id="dontask" checked={dontAsk} label={"Don't ask again"} />
          </div>
          <P>
            This will skip the confirmation popup when deleting decks, and will last for the
            duration of this session on Curiosa.
          </P>

          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </div>
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button variant="destructive" onClick={onSubmit}>
          Confirm Delete
        </Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
